import { useContext, useEffect, useState } from "react";
import { FeatureFlagContext } from "./FeatureFlagContext";

export type UseIsFeatureEnabled<Flags extends string> = (
  feature: Flags
) => boolean;

/**
 * React hook that checks if a feature flag is enabled and updates when flag status changes.
 * This is the untyped version of the hook that accepts any string as a feature flag name.
 * For type safety, it's recommended to cast this to a typed version using `UseIsFeatureEnabled<YourFlagType>`.
 *
 * @example
 * // In a shared module:
 * import { useIsFeatureEnabledUntyped, UseIsFeatureEnabled } from "@fideus/feature-flags/react";
 * import { FeatureFlag } from "@fideus/feature-flags";
 *
 * export const useIsFeatureEnabled =
 *   useIsFeatureEnabledUntyped as UseIsFeatureEnabled<FeatureFlag>;
 *
 * @example
 * // In a component:
 * const isMaintenanceMode = useIsFeatureEnabled("showMaintenancePageInClientPortal");
 *
 * @param feature - The name of the feature flag to check
 * @returns A boolean indicating whether the feature is enabled
 */
export const useIsFeatureEnabledUntyped: UseIsFeatureEnabled<never> = (
  feature
) => {
  const featureFlags = useContext(FeatureFlagContext);
  if (!featureFlags) {
    throw new TypeError("FeatureFlagContext not initialised");
  }
  const [isEnabled, setIsEnabled] = useState(
    featureFlags.isFeatureEnabled(feature)
  );

  useEffect(() => {
    featureFlags.onFeatureFlagsUpdated(() => {
      setIsEnabled(featureFlags.isFeatureEnabled(feature));
    });
  }, [featureFlags, feature]);

  return isEnabled;
};
